import React from "react";
import EngagementFunnelPanel from "../../customComponents/EnagementFunnelPanel";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useConfig } from "../../Config.hooks";
import { PageTitle } from "../../components";
import IncreaseAwarenessPanel from "../../customComponents/IncreaseAwarenessPanel";
import SiteTrafficPanel from "../../customComponents/SiteTrafficPanel";
import SiteInteractionPanel from "../../customComponents/SiteInteractionPanel";
import TinyDateRangePicker from "../../components/TinyDateRangePicker";
import { useDashboard } from "./Dashboard.hooks";
import { differenceInDays } from "date-fns";
import TopEngagedPanel from "../../customComponents/TopEngagedPanel/TopEngagedPanel";
import AccountInsightsPanel from "../../customComponents/AccountInsightsPanel";
import AiSummaryPanel from "../../customComponents/AiSummaryPanel";
import CustomerActivityPanel from "../../customComponents/CustomerActivityPanel/CustomerActivityPanel";
import { Plus, PlusCircle } from "@tiny/icons";
import { useLandingPage } from "../LandingPage/LandingPage.hooks";
import TotalOrdersPanel from "../../customComponents/TotalOrdersPanel";
import TotalRevenuePanel from "../../customComponents/TotalRevenuePanel";
import { useConfigView } from "../../Config.context";

function Dashboard() {
  const { selectedRange, handleDateChange } = useDashboard();
  const { config } = useConfigView();

  const dayDiff = differenceInDays(
    selectedRange.endDate,
    selectedRange.startDate
  );

  const groupTimeUnit = dayDiff > 31 ? "MONTH" : "DAY";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    anchorEl,
    handleCloseMenu,
    handleMenuOpen,
    open,
    onCreateCampaign,
    onCreateWorkflow,
  } = useLandingPage();

  return (
    <Box
      sx={{
        marginTop: { xs: config?.appName === "tinyEmail" ? 4 : 1, md: "auto" },
      }}
    >
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        flexDirection={"column"}
      >
        <PageTitle
          title={
            config?.appName === "tinyEmail" ? (
              <Typography variant="h1" m={0} color="grey.100" noWrap>
                Dashboard
              </Typography>
            ) : (
              <Typography variant="h2" m={0} color="grey.100" noWrap>
                Dashboard
              </Typography>
            )
          }
          actions={
            <>
              {!isMobile && (
                <TinyDateRangePicker
                  selectedRange={selectedRange}
                  onChange={handleDateChange}
                  maxDate={new Date()}
                />
              )}

              {!isMobile ? (
                <Button
                  color="primary"
                  variant="contained"
                  size="medium"
                  endIcon={<Plus />}
                  onClick={handleMenuOpen}
                  sx={{ ml: 2, pt: 2 }}
                >
                  Add New
                </Button>
              ) : (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleMenuOpen}
                  sx={{ ml: 1 }}
                >
                  <PlusCircle />
                </IconButton>
              )}

              <Menu
                id="dashboard-addnew-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                PaperProps={{ style: { marginTop: 8 } }}
              >
                <MenuItem
                  onClick={() => {
                    onCreateCampaign();
                    handleCloseMenu();
                  }}
                >
                  <Typography variant="p2">Campaign</Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    onCreateWorkflow();
                    handleCloseMenu();
                  }}
                >
                  <Typography variant="p2">Workflow</Typography>
                </MenuItem>
              </Menu>
            </>
          }
        />
        {isMobile && (
          <Box my={4} mx={{ xs: 2, md: "auto" }}>
            <TinyDateRangePicker
              selectedRange={selectedRange}
              onChange={handleDateChange}
              maxDate={new Date()}
            />
          </Box>
        )}
      </Box>

      <Grid container spacing={4}>
        {config?.appName === "tinyEmail" && (
          <Grid item xs={12}>
            <AiSummaryPanel />
          </Grid>
        )}
        <Grid item xs={12} data-tour="dashboard-engagement-funnel">
          <EngagementFunnelPanel
            groupTimeUnit={groupTimeUnit}
            from={selectedRange.startDate.getTime()}
            to={selectedRange.endDate.getTime()}
          />
        </Grid>

        {config?.shopifyIntegration && (
          <>
            {" "}
            <Grid item xs={12} md={6} data-tour="dashboard-total-revenue">
              <TotalRevenuePanel
                from={selectedRange.startDate.getTime()}
                to={selectedRange.endDate.getTime()}
              />
            </Grid>
            <Grid item xs={12} md={6} data-tour="dashboard-total-orders">
              <TotalOrdersPanel
                from={selectedRange.startDate.getTime()}
                to={selectedRange.endDate.getTime()}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} md={6} data-tour="dashboard-customer-activity">
          <CustomerActivityPanel />
        </Grid>
        <Grid item xs={12} md={6} data-tour="dashboard-account-insights">
          <AccountInsightsPanel />
        </Grid>

        <Grid item xs={12} md={4} data-tour="dashboard-awareness">
          <IncreaseAwarenessPanel
            groupTimeUnit={groupTimeUnit}
            from={selectedRange.startDate.getTime()}
            to={selectedRange.endDate.getTime()}
          />
        </Grid>

        <Grid item xs={12} md={4} data-tour="dashboard-site-traffic">
          <SiteTrafficPanel
            groupTimeUnit={groupTimeUnit}
            from={selectedRange.startDate.getTime()}
            to={selectedRange.endDate.getTime()}
          />
        </Grid>

        <Grid item xs={12} md={4} data-tour="dashboard-site-interaction">
          <SiteInteractionPanel
            groupTimeUnit={groupTimeUnit}
            from={selectedRange.startDate.getTime()}
            to={selectedRange.endDate.getTime()}
          />
        </Grid>
        <Grid item xs={12} data-tour="dashboard-top-engaged">
          <TopEngagedPanel />
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
